import { useEffect, useState } from "react";
import { api, extractError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { UserPlus, Trash2, Pencil, Users } from "lucide-react";

const ROLES = [
  { v: "employee", label: "Employé" },
  { v: "manager", label: "Responsable" },
  { v: "admin", label: "Administrateur" },
];

const roleColor = {
  admin: "bg-primary text-primary-foreground",
  manager: "bg-primary/10 text-primary border border-primary/30",
  employee: "bg-muted text-muted-foreground",
};

export default function TeamPage() {
  const [users, setUsers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "employee", manager_id: "", contract_status: "CDI" });

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); }, []);

  const managers = users.filter(u => u.role === "manager" || u.role === "admin");

  const createUser = async () => {
    try {
      await api.post("/users", { ...form, manager_id: form.manager_id || null });
      toast.success("Utilisateur créé");
      setOpenNew(false);
      setForm({ email: "", name: "", password: "", role: "employee", manager_id: "", contract_status: "CDI" });
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const saveEdit = async () => {
    try {
      const payload = {
        name: editing.name,
        role: editing.role,
        manager_id: editing.manager_id || null,
        initial_recovery_hours: parseFloat(editing.initial_recovery_hours) || 0,
        contract_status: editing.contract_status || null,
      };
      if (editing.newPassword) payload.password = editing.newPassword;
      await api.patch(`/users/${editing.id}`, payload);
      toast.success("Modifications enregistrées");
      setEditing(null);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const remove = async (u) => {
    if (!confirm(`Supprimer ${u.name} ?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Utilisateur supprimé"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Administration</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight">Équipe</h1>
          <p className="text-sm text-muted-foreground mt-1">Gérez les employés, responsables et administrateurs.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button className="rounded-full active:scale-95" data-testid="add-user-btn">
              <UserPlus className="h-4 w-4 mr-2" /> Ajouter un membre
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Nouveau membre</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nom complet</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
              <div><Label>Mot de passe temporaire</Label><Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
              <div>
                <Label>Rôle</Label>
                <Select value={form.role} onValueChange={v => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {form.role === "employee" && (
                <div>
                  <Label>Responsable direct</Label>
                  <Select value={form.manager_id || "none"} onValueChange={v => setForm({ ...form, manager_id: v === "none" ? "" : v })}>
                    <SelectTrigger data-testid="new-user-manager"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Statut du contrat</Label>
                <Select value={form.contract_status || "CDI"} onValueChange={v => setForm({ ...form, contract_status: v })}>
                  <SelectTrigger data-testid="new-user-contract"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Alternant">Alternant</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                    <SelectItem value="TNS">TNS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" className="rounded-full" onClick={() => setOpenNew(false)}>Annuler</Button>
              <Button className="rounded-full active:scale-95" onClick={createUser} data-testid="confirm-create-user">Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </header>

      <Card className="border-border/60 shadow-warm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/60">
                  <th className="py-3 px-5 font-medium">Membre</th>
                  <th className="py-3 px-5 font-medium">Rôle</th>
                  <th className="py-3 px-5 font-medium">Contrat</th>
                  <th className="py-3 px-5 font-medium">Responsable</th>
                  <th className="py-3 px-5 font-medium text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => {
                  const manager = users.find(x => x.id === u.manager_id);
                  return (
                    <tr key={u.id} className="border-b border-border/40 fade-up" data-testid={`user-row-${u.id}`}>
                      <td className="py-3 px-5">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center font-medium">
                            {u.name?.[0]?.toUpperCase() || "?"}
                          </div>
                          <div>
                            <div className="font-medium">{u.name}</div>
                            <div className="text-xs text-muted-foreground">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-5">
                        <Badge className={`${roleColor[u.role]} rounded-full px-2.5`}>{ROLES.find(r => r.v === u.role)?.label || u.role}</Badge>
                      </td>
                      <td className="py-3 px-5">
                        {u.contract_status ? <Badge variant="outline" className="rounded-full text-[10px]" data-testid={`user-contract-${u.id}`}>{u.contract_status}</Badge> : <span className="text-xs text-muted-foreground">—</span>}
                      </td>
                      <td className="py-3 px-5 text-muted-foreground">{manager?.name || "—"}</td>
                      <td className="py-3 px-5 text-right">
                        <Button variant="ghost" size="icon" onClick={() => setEditing({ ...u, newPassword: "" })} data-testid={`edit-user-${u.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" className="text-destructive hover:bg-destructive/10" onClick={() => remove(u)} data-testid={`delete-user-${u.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {users.length === 0 && (
                  <tr><td colSpan={5} className="py-10 text-center text-muted-foreground"><Users className="h-8 w-8 mx-auto mb-2 opacity-50" />Aucun membre</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifier {editing?.name}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><Label>Nom</Label><Input value={editing.name} onChange={e => setEditing({ ...editing, name: e.target.value })} data-testid="edit-user-name" /></div>
              <div>
                <Label>Rôle</Label>
                <Select value={editing.role} onValueChange={v => setEditing({ ...editing, role: v })}>
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>{ROLES.map(r => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Responsable direct</Label>
                <Select value={editing.manager_id || "none"} onValueChange={v => setEditing({ ...editing, manager_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="edit-user-manager"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {managers.filter(m => m.id !== editing.id).map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Nouveau mot de passe (laisser vide pour ne pas changer)</Label>
                <Input type="text" value={editing.newPassword} onChange={e => setEditing({ ...editing, newPassword: e.target.value })} data-testid="edit-user-password" />
              </div>
              <div>
                <Label>Statut du contrat</Label>
                <Select value={editing.contract_status || "CDI"} onValueChange={v => setEditing({ ...editing, contract_status: v })}>
                  <SelectTrigger data-testid="edit-user-contract"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CDI">CDI</SelectItem>
                    <SelectItem value="CDD">CDD</SelectItem>
                    <SelectItem value="Alternant">Alternant</SelectItem>
                    <SelectItem value="Stage">Stage</SelectItem>
                    <SelectItem value="TNS">TNS</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Solde de récupération initial (heures)</Label>
                <Input
                  type="number"
                  step="0.25"
                  value={editing.initial_recovery_hours ?? 0}
                  onChange={e => setEditing({ ...editing, initial_recovery_hours: e.target.value })}
                  data-testid="edit-user-initial-recovery"
                />
                <div className="text-[11px] text-muted-foreground mt-1">
                  Solde acquis avant la mise en place du nouveau système. S'ajoute au différentiel des semaines validées pour calculer le solde total.
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" className="rounded-full" onClick={() => setEditing(null)}>Annuler</Button>
            <Button className="rounded-full active:scale-95" onClick={saveEdit} data-testid="confirm-edit-user">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
